import { Link } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { Play, ArrowRight } from "lucide-react";
import PhoneMockup from "../components/PhoneMockup";
import { Reveal, MaskLine } from "../components/Reveal";

const STEPS = [
  {
    n: "1",
    title: "Host",
    copy: "Tap the center thumbprint, Name it, add a cover image, add music",
  },
  {
    n: "2",
    title: "Engage",
    copy: "Guests add music and react to songs",
  },
  {
    n: "3",
    title: "Share",
    copy: "Scan QR code, send a message or add by username.",
  },
];

const USECASES = [
  { title: "Listening Parties", copy: "Experience new releases, album drops, and favorite tracks together.", len: "3:42" },
  { title: "Game Day", copy: "Listen to your pregame music in real time with your fans.", len: "2:58" },
  { title: "Artist Engagement", copy: "Share your pre-show soundtrack with fans and listen together.", len: "4:15" },
  { title: "Music Battles", copy: "Bring artists, DJs, and fans together for music matchups.", len: "3:05" },
];

function SessionsHero() {
  return (
    <section className="relative overflow-hidden sessions-wash" data-testid="sessions-hero">
      <div className="relative mx-auto max-w-3xl px-5 sm:px-8 pt-36 sm:pt-40 pb-12 text-center">
        <h1 className="hero-title !text-[clamp(2.4rem,5.5vw,4rem)]" data-testid="sessions-title">
          <MaskLine delay={0.1}>
            Sessions <span className="bg-gradient-to-r from-[#2563EB] to-[#60A5FA] bg-clip-text text-transparent">Synced</span>
          </MaskLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.6 }}
          className="body-lg mt-6 max-w-xl mx-auto"
          data-testid="sessions-body"
        >
          Connect through music together or at a distance.
        </motion.p>
      </div>
    </section>
  );
}

function HowItWorks() {
  return (
    <section className="section !pt-16" data-testid="sessions-how">
      <div className="mx-auto max-w-5xl px-5 sm:px-8">
        <Reveal className="text-center">
          <h2 className="section-title" data-testid="sessions-how-title">
            How It <span className="bg-gradient-to-r from-[#2563EB] to-[#60A5FA] bg-clip-text text-transparent">Works</span>
          </h2>
          <p className="body-lg mt-4">Host sessions in three steps.</p>
        </Reveal>
        <div className="relative mt-14">
          <div
            className="hidden sm:block absolute top-[7px] left-[17%] right-[17%] h-[3px] rounded-full bg-gradient-to-r from-[#2563EB] via-[#3B82F6] to-[#60A5FA] opacity-70"
            aria-hidden="true"
          >
            <span className="timeline-pulse" />
          </div>
          <div className="grid sm:grid-cols-3 gap-10 sm:gap-8">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.09} className="text-center" testid={`sessions-step-${i}`}>
                <span
                  className="step-node step-node-blue relative z-10 grid place-items-center w-4 h-4 mx-auto rounded-full bg-gradient-to-br from-[#2563EB] to-[#60A5FA] shadow-[0_0_16px_rgba(37,99,235,0.7)] ring-4 ring-[rgb(var(--c-ink))]"
                  style={{ animationDelay: `${i * 2}s` }}
                />
                <span className="block font-display text-5xl font-semibold text-[#BFDBFE] mt-5">{s.n}</span>
                <h3 className="font-display text-lg font-semibold mt-2">{s.title}</h3>
                <p className="text-[0.92rem] leading-relaxed text-dusk mt-2 max-w-[260px] mx-auto">{s.copy}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function PerfectSync() {
  const [active, setActive] = useState(0);
  return (
    <section className="section sessions-wash-blend !pt-6" data-testid="sessions-sync">
      <div className="mx-auto max-w-6xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <div className="max-w-xl" data-testid="sessions-queue">
            <div className="flex items-baseline justify-between px-3 pb-1">
              <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#60A5FA]/70">Up next</span>
              <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-[#60A5FA]/50">4 moments</span>
            </div>
            {USECASES.map((u, i) => (
              <Reveal key={u.title} delay={0.1 + i * 0.06} testid={`usecase-card-${u.title.toLowerCase().replace(/\s+/g, "-")}`}>
                <div
                  className="queue-row group grid grid-cols-[auto_1fr_auto] items-start gap-4 px-3 py-4 rounded-xl border-t border-[#3B82F6]/20 cursor-pointer select-none"
                  role="button"
                  tabIndex={0}
                  aria-pressed={active === i}
                  onClick={() => setActive(i)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setActive(i);
                    }
                  }}
                  data-testid={`queue-line-${i}`}
                >
                  <div className="relative w-7 pt-1">
                    <span className="queue-num block text-[13px] font-mono text-[#60A5FA]/70">{String(i + 1).padStart(2, "0")}</span>
                    <span className="queue-play absolute inset-0 grid place-items-center text-[#60A5FA]">
                      <Play size={14} fill="currentColor" />
                    </span>
                  </div>
                  <div>
                    <h3 className="font-display text-lg sm:text-xl font-semibold flex items-center gap-2.5">
                      {u.title}
                      {active === i && (
                        <span className="eq" aria-hidden="true" data-testid="queue-eq">
                          <span />
                          <span />
                          <span />
                        </span>
                      )}
                    </h3>
                    <p className="text-[0.9rem] leading-relaxed text-dusk mt-1">{u.copy}</p>
                  </div>
                  <span className="pt-1 text-[12px] font-mono text-[#60A5FA]/60">{u.len}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </Reveal>
        <Reveal delay={0.12} className="flex justify-center lg:justify-end">
          <div className="relative">
            <div className="glow-blob glow-azure" style={{ width: 340, height: 340, top: "8%", left: "50%", transform: "translateX(-50%)" }} aria-hidden="true" />
            <PhoneMockup variant="live" className="relative" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function HostCta() {
  return (
    <section className="section !py-20" data-testid="sessions-cta-section">
      <div className="mx-auto max-w-3xl px-5 sm:px-8 text-center">
        <Reveal>
          <h2 className="section-title" data-testid="sessions-cta-title">
            Host a <span className="bg-gradient-to-r from-[#2563EB] to-[#60A5FA] bg-clip-text text-transparent">Session</span>
          </h2>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <Link to="/access" data-testid="sessions-download-cta" className="btn-primary">
              <Play size={18} fill="currentColor" /> Download Tap In
            </Link>
            <Link to="/playlists" data-testid="sessions-playlists-cta" className="sessions-pill-ghost !border-grape/40 !text-grape-soft">
              Playlists <ArrowRight size={18} />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Sessions() {
  return (
    <>
      <SessionsHero />
      <PerfectSync />
      <HowItWorks />
      <section className="section !pt-12" data-testid="artist-spotlight">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          <Reveal delay={0.1}>
            <div className="relative mt-0 rounded-3xl bg-[#3B82F6]/[0.06] border border-[#3B82F6]/20 p-6 sm:p-10 overflow-hidden" data-testid="artist-card">
              <div className="glow-blob glow-azure" style={{ width: 320, height: 320, bottom: "-20%", right: "-5%" }} aria-hidden="true" />
              <div className="relative grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h3 className="font-display text-4xl sm:text-5xl font-semibold" data-testid="artist-name">22WVY</h3>
                  <p className="mt-5 text-[0.95rem] leading-relaxed text-dusk" data-testid="artist-copy">
                    Tap In connects listeners with rising artists like 22 WVY, letting them share
                    their sound, grow their audience, and make music discovery more interactive
                    and social.
                  </p>
                  <div className="flex gap-10 mt-8" data-testid="artist-stats">
                    {[
                      ["4M+", "Streams"],
                      ["30K", "Monthly Listeners"],
                      ["5", "Projects"],
                    ].map(([num, label]) => (
                      <div key={label}>
                        <p className="font-display text-2xl sm:text-3xl font-semibold text-[#3B82F6]">{num}</p>
                        <p className="text-xs text-dusk mt-0.5">{label}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex gap-4 justify-center items-stretch">
                  <img
                    src="/brand/artist-22wvy-dashboard.jpg"
                    alt="Maria — 1,111,075 all-time streams"
                    className="w-[46%] rounded-2xl ring-1 ring-[#3B82F6]/25 shadow-card object-cover"
                    data-testid="artist-dashboard"
                  />
                  <img
                    src="/brand/artist-22wvy-live.jpg"
                    alt="22WVY performing live"
                    className="w-[50%] rounded-2xl ring-1 ring-[#3B82F6]/25 shadow-card object-cover"
                    data-testid="artist-live"
                  />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <HostCta />
    </>
  );
}
